/**
 * HeroVideoBlock — inline video that sits below the landing hero.
 *
 * Content is admin-controlled via /api/settings/hero-video. When the admin
 * has no video configured or has disabled it, the whole block collapses so
 * the landing page reads naturally without it.
 */
import React, { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Play } from 'lucide-react';
import { AuthContext } from '../App';

const HeroVideoBlock = () => {
  const { API } = useContext(AuthContext);
  const [setting, setSetting] = useState(null);
  const [started, setStarted] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/settings/hero-video`)
      .then((r) => { if (!cancelled) setSetting(r.data); })
      .catch(() => { if (!cancelled) setSetting(null); });
    return () => { cancelled = true; };
  }, [API]);

  if (!setting || !setting.enabled || !setting.video_url) return null;

  const play = () => {
    setStarted(true);
    // Give the ref a tick to bind before calling play().
    setTimeout(() => { videoRef.current?.play?.().catch(() => {}); }, 0);
  };

  return (
    <section className="container mx-auto px-6 -mt-4 mb-16" data-testid="landing-hero-video">
      <div className="max-w-4xl mx-auto">
        {(setting.title || setting.subtitle) && (
          <div className="text-center mb-4">
            {setting.title && (
              <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-foreground">
                {setting.title}
              </h2>
            )}
            {setting.subtitle && (
              <p className="mt-2 text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">
                {setting.subtitle}
              </p>
            )}
          </div>
        )}
        <div className="relative rounded-3xl overflow-hidden bg-black aspect-video shadow-2xl border border-primary/20 ring-1 ring-black/5">
          {(!started && !setting.autoplay) ? (
            <button
              type="button"
              onClick={play}
              className="absolute inset-0 w-full h-full group"
              data-testid="hero-video-play-btn"
              aria-label="Play Freeze Dry Academy video"
            >
              {setting.poster_url ? (
                <img src={setting.poster_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <video
                  src={setting.video_url}
                  className="w-full h-full object-cover"
                  muted
                  preload="metadata"
                  playsInline
                />
              )}
              <span className="absolute inset-0 bg-navy/40 group-hover:bg-navy/20 transition-colors" />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="h-20 w-20 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                  <Play className="w-8 h-8 ml-1" fill="currentColor" />
                </span>
              </span>
            </button>
          ) : (
            <video
              ref={videoRef}
              src={setting.video_url}
              poster={setting.poster_url || undefined}
              className="w-full h-full object-cover"
              controls
              autoPlay={setting.autoplay || started}
              playsInline
              data-testid="hero-video-el"
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default HeroVideoBlock;
